import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { FileText, Download } from 'lucide-react';
import { db } from '../../firebase/config';
import { generarFactura, generarNombreFactura } from '../../utils/generarFactura';
import { notifyError, notifySuccess } from '../../utils/alerts';

const getToday = () => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
};

const toLocalDate = (value) => new Date(`${value}T12:00:00`);

const GenerarPDF = () => {
    const [students, setStudents] = useState([]);
    const [loadingStudents, setLoadingStudents] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [selectedStudentId, setSelectedStudentId] = useState('');
    const [studentSearch, setStudentSearch] = useState('');
    const [studentListOpen, setStudentListOpen] = useState(false);
    const [form, setForm] = useState({
        acudienteNombre: '',
        acudienteTelefono: '',
        grado: '',
        monto: '',
        dias: '30',
        fechaEmision: getToday(),
        fechaVencimiento: getToday(),
        metodoPago: 'Transferencia / Comprobante Digital',
        referencia: String(Date.now())
    });

    useEffect(() => {
        const loadStudents = async () => {
            try {
                const snapshot = await getDocs(collection(db, 'ninos'));
                setStudents(snapshot.docs.map(student => ({ id: student.id, ...student.data() })));
            } catch (error) {
                console.error('No se pudieron cargar los estudiantes para generar el PDF:', error);
                notifyError('Error', 'No se pudieron cargar los estudiantes.');
            } finally {
                setLoadingStudents(false);
            }
        };

        loadStudents();
    }, []);

    const updateField = (event) => {
        const { name, value } = event.target;
        setForm(current => ({ ...current, [name]: value }));
    };

    const handleStudentChange = (event) => {
        const studentId = event.target.value;
        const student = students.find(item => item.id === studentId);
        setSelectedStudentId(studentId);
        setStudentSearch(student?.nombre || '');
        setStudentListOpen(false);
        if (!student) {
            setForm(current => ({
                ...current,
                acudienteNombre: '',
                acudienteTelefono: '',
                grado: '',
                monto: ''
            }));
            return;
        }

        setForm(current => ({
            ...current,
            acudienteNombre: student.acudientes?.[0]?.nombre || '',
            acudienteTelefono: student.acudientes?.[0]?.telefono || '',
            grado: student.grado || student.nivel || '',
            monto: String(student.valorMensualidad || ''),
            fechaVencimiento: student.proximaFechaPago || getToday()
        }));
    };

    const handleGenerate = (event) => {
        event.preventDefault();
        const student = students.find(item => item.id === selectedStudentId);
        const amount = Number(form.monto);
        const approvedDays = Number(form.dias);

        if (!student || !form.fechaEmision || !form.fechaVencimiento || !form.referencia.trim()) {
            notifyError('Datos incompletos', 'Selecciona un estudiante y completa las fechas y la referencia.');
            return;
        }
        if (!Number.isFinite(amount) || amount <= 0 || !Number.isInteger(approvedDays) || approvedDays <= 0) {
            notifyError('Datos inválidos', 'El monto y los días deben ser mayores que cero.');
            return;
        }

        setGenerating(true);
        try {
            const issueDate = toLocalDate(form.fechaEmision);
            const dueDate = toLocalDate(form.fechaVencimiento);
            const payment = {
                id: form.referencia.trim(),
                fechaEnvio: issueDate.toISOString(),
                monto: amount,
                diasAprobados: approvedDays,
                tipoPago: form.metodoPago.trim() || 'Transferencia / Comprobante Digital'
            };
            const studentData = {
                ...student,
                grado: form.grado,
                acudientes: [{
                    ...student.acudientes?.[0],
                    nombre: form.acudienteNombre,
                    telefono: form.acudienteTelefono
                }]
            };
            const pdf = generarFactura(payment, studentData, dueDate, approvedDays);
            const fileUrl = URL.createObjectURL(pdf);
            const downloadLink = document.createElement('a');
            downloadLink.href = fileUrl;
            downloadLink.download = generarNombreFactura(student.nombre, issueDate);
            document.body.appendChild(downloadLink);
            downloadLink.click();
            downloadLink.remove();
            window.setTimeout(() => URL.revokeObjectURL(fileUrl), 1000);
            notifySuccess('PDF generado', 'El comprobante se descargó correctamente.');
        } catch (error) {
            console.error('No se pudo generar el PDF:', error);
            notifyError('Error', 'No se pudo generar el comprobante PDF.');
        } finally {
            setGenerating(false);
        }
    };

    const filteredStudents = students.filter(student =>
        student.nombre?.toLocaleLowerCase('es').includes(studentSearch.trim().toLocaleLowerCase('es'))
    );

    const inputClass = 'w-full rounded-2xl bg-gray-50 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-blue-500';
    const labelClass = 'mb-2 block text-xs font-black uppercase tracking-wide text-gray-500';

    return (
        <div className="mx-auto max-w-5xl space-y-6 pb-10">
            <header>
                <h1 className="text-2xl font-black tracking-tight text-gray-800 sm:text-3xl">Generar PDF</h1>
                <p className="mt-1 text-sm font-medium text-gray-500">
                    Crea un comprobante con la plantilla de pagos y personaliza sus datos antes de descargarlo.
                </p>
            </header>

            <form onSubmit={handleGenerate} className="space-y-6 rounded-[2rem] border border-gray-100 bg-white p-5 shadow-sm sm:p-8">
                <section>
                    <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-gray-800">
                        <FileText className="text-blue-600" size={20} /> Estudiante y acudiente
                    </h2>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <label>
                            <span className={labelClass}>Estudiante</span>
                            <div
                                className="relative"
                                onBlur={(event) => {
                                    if (!event.currentTarget.contains(event.relatedTarget)) setStudentListOpen(false);
                                }}
                            >
                                <input
                                    type="text"
                                    role="combobox"
                                    aria-autocomplete="list"
                                    aria-expanded={studentListOpen}
                                    aria-controls="generar-pdf-estudiantes"
                                    autoComplete="off"
                                    required
                                    value={studentSearch}
                                    onFocus={() => setStudentListOpen(true)}
                                    onClick={() => setStudentListOpen(true)}
                                    onChange={(event) => {
                                        setStudentSearch(event.target.value);
                                        setSelectedStudentId('');
                                        setStudentListOpen(true);
                                    }}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Escape') setStudentListOpen(false);
                                    }}
                                    disabled={loadingStudents}
                                    placeholder={loadingStudents ? 'Cargando estudiantes...' : 'Escribe para buscar estudiante...'}
                                    className={inputClass}
                                />
                                {studentListOpen && (
                                    <div id="generar-pdf-estudiantes" role="listbox" className="absolute z-20 mt-2 max-h-56 w-full overflow-y-auto rounded-2xl border border-gray-200 bg-white py-1 shadow-xl">
                                        {filteredStudents.length ? filteredStudents.map(student => (
                                            <button
                                                key={student.id}
                                                type="button"
                                                role="option"
                                                aria-selected={selectedStudentId === student.id}
                                                onClick={() => handleStudentChange({ target: { value: student.id } })}
                                                className="block w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none"
                                            >
                                                {student.nombre}
                                            </button>
                                        )) : (
                                            <p className="px-4 py-3 text-sm text-gray-500">
                                                {loadingStudents ? 'Cargando estudiantes...' : 'No se encontraron estudiantes.'}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </label>
                        <label>
                            <span className={labelClass}>Grado o nivel</span>
                            <input value={form.grado} onChange={updateField} name="grado" className={inputClass} />
                        </label>
                        <label>
                            <span className={labelClass}>Nombre del acudiente</span>
                            <input value={form.acudienteNombre} onChange={updateField} name="acudienteNombre" className={inputClass} />
                        </label>
                        <label>
                            <span className={labelClass}>Teléfono del acudiente</span>
                            <input value={form.acudienteTelefono} onChange={updateField} name="acudienteTelefono" className={inputClass} />
                        </label>
                    </div>
                </section>

                <section>
                    <h2 className="mb-4 text-lg font-bold text-gray-800">Datos del comprobante</h2>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <label>
                            <span className={labelClass}>Monto pagado ($)</span>
                            <input required type="number" min="1" value={form.monto} onChange={updateField} name="monto" className={inputClass} />
                        </label>
                        <label>
                            <span className={labelClass}>Días aprobados</span>
                            <input required type="number" min="1" step="1" value={form.dias} onChange={updateField} name="dias" className={inputClass} />
                        </label>
                        <label>
                            <span className={labelClass}>Fecha de emisión</span>
                            <input required type="date" value={form.fechaEmision} onChange={updateField} name="fechaEmision" className={inputClass} />
                        </label>
                        <label>
                            <span className={labelClass}>Próxima fecha de vencimiento</span>
                            <input required type="date" value={form.fechaVencimiento} onChange={updateField} name="fechaVencimiento" className={inputClass} />
                        </label>
                        <label>
                            <span className={labelClass}>Método de pago</span>
                            <input value={form.metodoPago} onChange={updateField} name="metodoPago" className={inputClass} />
                        </label>
                        <label>
                            <span className={labelClass}>Referencia del comprobante</span>
                            <input required value={form.referencia} onChange={updateField} name="referencia" className={inputClass} />
                        </label>
                    </div>
                    <p className="mt-3 text-xs text-gray-500">
                        La plantilla, el logo y los datos institucionales se mantienen como en los comprobantes actuales.
                        Este módulo no registra ni modifica pagos.
                    </p>
                </section>

                <div className="flex flex-col gap-3 sm:flex-row">
                    <button
                        type="submit"
                        disabled={loadingStudents || generating || students.length === 0}
                        className="flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 px-6 py-4 font-bold text-white shadow-lg transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                    >
                        <Download size={20} />
                        {generating ? 'Generando...' : 'Generar y descargar PDF'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default GenerarPDF;
